/**
 * Web Push helpers for the Talos SPA.
 *
 * Handles service-worker registration, subscribing/unsubscribing, and keeping
 * the worker informed of the currently-focused session so the OS notification
 * is suppressed when the user is already viewing that session.
 */
import { getPushVapidKey, savePushSubscription, deletePushSubscription } from './api.js';

const SW_PATH = '/sw.js';

// pushSupported reports whether the browser supports web push + service workers.
export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// registerServiceWorker registers the push service worker (no-op if already
// registered or unsupported).
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (err) {
    console.warn('service worker registration failed', err);
    return null;
  }
}

export async function getRegistration() {
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

// base64UrlToUint8Array decodes a base64url VAPID public key into the typed
// array expected by PushManager.subscribe.
export function base64UrlToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

// getcurrentSubscription returns the active browser push subscription, or null.
export async function getSubscription() {
  if (!pushSupported()) return null;
  const reg = await getRegistration();
  return reg.pushManager.getSubscription();
}

// subscribe requests push permission, subscribes using the server's VAPID key,
// and persists the subscription on the backend.
export async function subscribe() {
  if (!pushSupported()) throw new Error('Web push is not supported in this browser');

  const reg = await getRegistration();
  const [publicKey, existing] = await Promise.all([getPushVapidKey(), reg.pushManager.getSubscription()]);
  const applicationServerKey = base64UrlToUint8Array(publicKey);

  const subscription = existing || (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  }));

  await savePushSubscription(subscription.toJSON());
  return subscription;
}

// unsubscribe removes the active push subscription locally and on the backend.
export async function unsubscribe() {
  const subscription = await getSubscription();
  if (!subscription) return null;
  await subscription.unsubscribe();
  try {
    await deletePushSubscription(subscription.endpoint);
  } catch (err) {
    // A local unsubscribe is still valid; surface the backend failure only as
    // a warning so the UI can continue.
    console.warn('could not delete backend subscription', err);
  }
  return true;
}

// currentFocusedSession derives the session ID being viewed from the hash
// route, or null when on a non-session page or the tab is hidden.
function currentFocusedSession() {
  if (document.visibilityState !== 'visible') return null;
  const m = window.location.hash.match(/#\/s\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// sendFocus notifies the active service worker controller of the focused
// session (or null). Called on hash changes, visibility changes, and once the
// worker gains control.
function sendFocus() {
  const controller = navigator.serviceWorker && navigator.serviceWorker.controller;
  if (controller) {
    controller.postMessage({ type: 'focus', sessionID: currentFocusedSession() });
  }
}

// initFocusSync registers the worker and keeps its focused-session state in
// sync with the page route and visibility. It also handles the worker's
// "resubscribe" message (triggered on pushsubscriptionchange).
export function initFocusSync() {
  if (!('serviceWorker' in navigator)) return;

  registerServiceWorker();

  window.addEventListener('hashchange', sendFocus);
  document.addEventListener('visibilitychange', sendFocus);

  navigator.serviceWorker.addEventListener('controllerchange', sendFocus);
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'resubscribe') {
      subscribe().catch((err) => console.warn('resubscribe failed', err));
    }
  });

  // Post the initial focus once the worker is active.
  navigator.serviceWorker.ready.then(() => sendFocus());
}
